import { Backdrop, SvgIcon, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { NavigationDrawer } from '@/widgets/NavigationDrawer';
import { Layout, RequestNewLink, Title } from '@/features/auth';
import {
  selectActiveOrganizationId,
  selectIsAuthorized,
  selectIsDisabled,
  selectIsSessionLoading,
  selectIsSessionRequested,
  setupProfile,
} from '@/entities/session';
import { baseApi } from '@/shared/api';
import { ROUTES } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Spinner, Page, Panel, NoItemsPlaceholder } from '@/shared/ui';

export function AuthGuard() {
  const dispath = useAppDispatch();
  const isSessionRequested = useAppSelector(selectIsSessionRequested);
  const isSessionLoading = useAppSelector(selectIsSessionLoading);
  const isAuthorized = useAppSelector(selectIsAuthorized);
  const isDisabled = useAppSelector(selectIsDisabled);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const location = useLocation();

  useEffect(() => {
    if (isSessionRequested) {
      return;
    }

    dispath(setupProfile());
  }, [isSessionRequested, dispath]);
  useEffect(() => {
    const { data } = baseApi.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' && globalThis.document.hidden) {
        globalThis.location.reload();
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  if (isSessionLoading || !isSessionRequested) {
    return (
      <Backdrop open>
        <Spinner />
      </Backdrop>
    );
  } else if (!isAuthorized) {
    if (isExpiredOTP(location.hash)) {
      Analytics.sendFeatureUsage('expired_auth_link', 'view_navigated');

      return (
        <Layout>
          <Title>Link expired</Title>
          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
            The link you clicked has expired. Enter your email address below to
            receive a new link.
          </Typography>
          <RequestNewLink />
        </Layout>
      );
    }

    return <Navigate to={ROUTES.signin} />;
  } else if (isDisabled) {
    return (
      <Page.Layout
        navigationSlot={<NavigationDrawer />}
        slotProps={{
          content: {
            sx: {
              display: 'flex',
              gap: 1,
            },
          },
        }}
      >
        <Panel
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NoItemsPlaceholder
            icon={
              <SvgIcon
                sx={{ width: 100, height: 100, color: 'text.secondary' }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                  ></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </SvgIcon>
            }
            title="Account disabled"
            description="Please contact your administrator to activate your account."
          />
        </Panel>
      </Page.Layout>
    );
  }

  return <Outlet key={activeOrganizationId} />;
}

const isExpiredOTP = (hash: string) => {
  const parsedHash = new URLSearchParams(hash.substring(1));

  return (
    parsedHash.get('error') === 'access_denied' &&
    parsedHash.get('error_code') === 'otp_expired'
  );
};
